/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { openGlobalModal, IModalData, IDialogOptions } from 'app/shared/elements/ess-global-modal';
import { isNullOrUndefined } from 'app/shared/lib/utilities';
import { IContactLogConfig } from 'app/core/models/essette-interfaces';
import { guid } from 'app/shared/lib/guid';
import * as CD from 'app/cd/models/essette-api';

interface IMemberSummaryModelData {
    member: CD.Member,
    readonly?: boolean;
}

@autoinject()
export class MemberSummary extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView })
    protected memberId: string | null = null;

    protected memberSummarySections: Record<string, boolean> = {};

    protected readonly: boolean = true;

    protected contactLogConfig: IContactLogConfig = {
        rollupConfig: {
            serviceRootUrl: 'cd/members',
            targetType: 'CD.Member'
        }
    };

    protected showMemberInformation: boolean = false;
    protected showAppealsAndGrievances: boolean = false;
    protected showProviders: boolean = false;
    protected showCaseManagement: boolean = false;
    protected showCustomerService: boolean = false;
    protected showUtilizationManagement: boolean = false;
    protected showClaims: boolean = false;
    protected showPopulationManagement: boolean = false;
    protected showHealthLevel7: boolean = false;
    protected showLabResults: boolean = false;
    protected showPharmacy: boolean = false;
    protected showContactLog: boolean = false;
    protected showImportedAuthorizations: boolean = false;
    protected showNotes: boolean = false;
    protected showDocuments: boolean = false;
    protected showCorrespondence: boolean = false;

    protected id: guid = guid();

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(...rest: any[]) {
        super(...rest);
        this.checkPermissions();
    }

    protected async activate(model: IMemberSummaryModelData): Promise<void> {
        if(!model) return;

        if(!isNullOrUndefined(model.readonly)) this.readonly = model.readonly;

        if(model.member?.id) this.memberId = model.member.id;

        this.checkPermissions();
    }

    ////****************** Events ***************************************************////
    protected openSection(sectionId: string): void {
        const section = sectionId.replace('-collapsible', '');
        this.memberSummarySections[section] = true;
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();

        this.showMemberInformation = this.accountContext.hasPermission('CD.Member.Duty.Member360.MemberInfo');

        if(this.env.config.modules.includes('cm')) {
            this.showCaseManagement = this.accountContext.hasPermission('CD.Member.Duty.Member360.CM');
        }
        if(this.env.config.modules.includes('um')) {
            this.showUtilizationManagement = this.accountContext.hasPermission('CD.Member.Duty.Member360.UM')
        }
        if(this.env.config.modules.includes('pm')) {
            this.showPopulationManagement = this.accountContext.hasPermission('CD.Member.Duty.Member360.PM')
        }
        if(this.env.config.modules.includes('cs')) {
            this.showCustomerService = this.accountContext.hasPermission('CD.Member.Duty.Member360.CS')
        }
        if(this.env.config.modules.includes('hl7')) {
            this.showHealthLevel7 = this.accountContext.hasPermission('CD.Member.Duty.Member360.HL7')
        }

        this.showAppealsAndGrievances = this.accountContext.hasPermission('CD.Member.Duty.Member360.AG')
        this.showProviders = this.accountContext.hasPermission('CD.Member.Duty.Member360.Providers');
        this.showClaims = this.accountContext.hasPermission('CD.Member.Duty.Member360.Claims');
        this.showLabResults = this.accountContext.hasPermission('CD.Member.Duty.Member360.Labs');
        this.showPharmacy = this.accountContext.hasPermission('CD.Member.Duty.Member360.Pharmacy');
        this.showContactLog = this.accountContext.hasPermission('CD.Member.Duty.Member360.ContactLog');
        this.showImportedAuthorizations = this.accountContext.hasPermission('CD.Member.Duty.Member360.ImportedAuths')
        this.showNotes = this.accountContext.hasPermission('CD.Member.Duty.Member360.Notes')
        this.showDocuments = this.accountContext.hasPermission('CD.Member.Duty.Member360.Documents')
        this.showCorrespondence = this.accountContext.hasPermission('CD.Member.Duty.Member360.Correspondence')
    }

    ////****************** Service Calls ********************************************////


}

/**
 * Opens the member "360" summary modal with the global dialog
 * @param member The member information to use
 */
export function openMemberSummary(member: CD.Member, readonly: boolean = true, moduleCode = 'cd'): void {
    if(!member || !member.id) {
        throw new Error('Member is missing or invalid');
    }
    if(document.documentURI.includes("#/cm/")) {
        moduleCode = "cm";
    }
    else if(document.documentURI.includes("#/um/")) {
        moduleCode = "um";
    }

    const dialogOptions: IDialogOptions = {
        width: '10',
        height: '10',
        helpTopic: `${moduleCode}.member-summary`,
        title: {
            key: 'cd:routes.member.summary.$title',
            replace: { display: member.$display }
        },
        print: true,
        popout: {
            route: `cd.member.summary`,
            params: { memberId: member.id }
        }
    };
    const modelData: IMemberSummaryModelData = {
        member: member,
        readonly: readonly
    };

    const modalData: IModalData = {
        componentId: 'app/cd/components/summary/member/member-summary',
        modelData: modelData,
        dialogOptions: dialogOptions
    };

    openGlobalModal(modalData);
}
